// src/components/layout/NavbarNotifications.js
//
// Calm, ops-priority notification bell that lives in the top navbar. The
// compact bell preserves the existing premium look (rounded square button,
// small red badge), while the dropdown panel translates each ops alert
// into an action label the team can act on without thinking — POST
// TASK/GIG, START DRIVER OUTREACH, PLACE ORDER, REPLY TO INQUIRY,
// IMMEDIATE RESPONSE NEEDED.
//
// Key design rules (see plan):
//   • Pulse only on super-urgent rows (and a single small dot on the bell
//     when at least one super-urgent item exists). Never the whole chip
//     or the whole bell.
//   • Zero-count items render in the dropdown as calm "all clear" rows
//     so operators see the full action surface, but the bell badge only
//     counts super + medium urgency.
//   • Keyboard: Esc to close, ArrowUp/ArrowDown to roam rows, Enter to
//     activate. Click-away closes via the same overlay pattern Header.js
//     already uses.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBell,
  FiCheckCircle,
  FiChevronRight,
  FiHelpCircle,
  FiInbox,
  FiMapPin,
  FiTruck,
} from 'react-icons/fi';
import { useOpsNotificationsData } from '../../hooks/useOpsNotifications';

const ICON_MAP = {
  bell: FiBell,
  inbox: FiInbox,
  truck: FiTruck,
  mapPin: FiMapPin,
  help: FiHelpCircle,
};

// Tone classes. Rose / red are reserved for super-urgent. Emerald is the
// calmer "still acted on this week" 3-day urgent. Indigo / amber / violet
// are the medium tones per category. Zinc is the all-clear tone.
const TONE_CLASSES = {
  rose: {
    chip: 'bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800/60',
    icon: 'text-rose-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-rose-600 dark:text-rose-300',
    count: 'text-rose-700 dark:text-rose-300',
    dot: 'text-rose-500',
  },
  red: {
    chip: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800/60',
    icon: 'text-red-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-red-600 dark:text-red-300',
    count: 'text-red-700 dark:text-red-300',
    dot: 'text-red-500',
  },
  amber: {
    chip: 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800/60',
    icon: 'text-amber-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-amber-700 dark:text-amber-300',
    count: 'text-amber-800 dark:text-amber-300',
    dot: 'text-amber-500',
  },
  violet: {
    chip: 'bg-violet-50 border-violet-200 dark:bg-violet-900/20 dark:border-violet-800/60',
    icon: 'text-violet-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-violet-700 dark:text-violet-300',
    count: 'text-violet-700 dark:text-violet-300',
    dot: 'text-violet-500',
  },
  indigo: {
    chip: 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800/60',
    icon: 'text-indigo-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-indigo-700 dark:text-indigo-300',
    count: 'text-indigo-700 dark:text-indigo-300',
    dot: 'text-indigo-500',
  },
  emerald: {
    chip: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800/60',
    icon: 'text-emerald-500',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-emerald-700 dark:text-emerald-300',
    count: 'text-emerald-700 dark:text-emerald-300',
    dot: 'text-emerald-500',
  },
  gray: {
    chip: 'bg-zinc-50 border-zinc-200 dark:bg-zinc-900/30 dark:border-zinc-800/60',
    icon: 'text-zinc-400',
    iconBg: 'bg-white/70 dark:bg-white/10',
    label: 'text-zinc-500 dark:text-zinc-400',
    count: 'text-zinc-600 dark:text-zinc-300',
    dot: 'text-zinc-400',
  },
};

function toneClasses(tone) {
  return TONE_CLASSES[tone] || TONE_CLASSES.gray;
}

function NotificationRow({ item, onActivate, isFocused, onFocus, focusRef }) {
  const Icon = ICON_MAP[item.iconName] || FiBell;
  const isZero = item.count === 0;
  const isSuper = item.urgency === 'super' && !isZero;
  const tone = toneClasses(isZero ? 'gray' : item.tone);

  return (
    <button
      ref={focusRef}
      type="button"
      role="menuitem"
      tabIndex={isFocused ? 0 : -1}
      onClick={() => onActivate(item)}
      onMouseEnter={onFocus}
      onFocus={onFocus}
      className={`
        group relative flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left
        transition-all hover:shadow-sm
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1
        dark:focus:ring-offset-zinc-900
        ${tone.chip}
      `}
      aria-label={`${item.label} — ${item.count} ${
        item.count === 1 ? 'item' : 'items'
      }. ${item.helperText}.`}
    >
      <div
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${tone.iconBg} ${tone.icon}`}
      >
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-[10.5px] font-semibold uppercase tracking-wider ${tone.label}`}
        >
          {item.label}
        </p>
        <div className="flex items-baseline gap-2">
          <span className={`text-lg font-bold leading-none ${tone.count}`}>
            {item.count}
          </span>
          <span className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">
            {item.helperText}
          </span>
        </div>
      </div>

      {isSuper ? (
        <span
          className={`relative inline-flex h-2 w-2 flex-shrink-0 ${tone.dot}`}
          aria-hidden="true"
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
        </span>
      ) : null}

      <FiChevronRight className="h-4 w-4 flex-shrink-0 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-200" />
    </button>
  );
}

export default function NavbarNotifications() {
  const navigate = useNavigate();
  const { items, totalActionable, hasSuperUrgent } = useOpsNotificationsData();

  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttonRef = useRef(null);
  const rowRefs = useRef([]);

  rowRefs.current = items.map(
    (_, i) => rowRefs.current[i] || React.createRef()
  );

  const close = useCallback(() => {
    setOpen(false);
    // Restore focus to the bell so the keyboard journey stays predictable.
    window.requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
  }, []);

  const activate = useCallback(
    (item) => {
      setOpen(false);
      navigate(item.route);
    },
    [navigate]
  );

  // Esc + ArrowUp/ArrowDown roving focus while open.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((idx) => Math.min(items.length - 1, idx + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((idx) => Math.max(0, idx - 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setFocusIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setFocusIndex(items.length - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, items.length, close]);

  // Move DOM focus when focusIndex changes so screen readers + keyboard
  // users actually see the highlighted row.
  useEffect(() => {
    if (!open) return;
    const node = rowRefs.current[focusIndex]?.current;
    if (node && typeof node.focus === 'function') {
      node.focus({ preventScroll: false });
    }
  }, [focusIndex, open, items.length]);

  // Reset focus to the first row whenever the panel opens.
  useEffect(() => {
    if (open) setFocusIndex(0);
  }, [open]);

  const ariaLabel = `Open ops notifications, ${totalActionable} action ${
    totalActionable === 1 ? 'item' : 'items'
  }`;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2.5 rounded-xl text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FiBell className="w-5 h-5" />
        {totalActionable > 0 ? (
          <span
            className={`absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${
              hasSuperUrgent ? 'bg-rose-500' : 'bg-zinc-700'
            }`}
          >
            {totalActionable > 99 ? '99+' : totalActionable}
          </span>
        ) : null}
        {hasSuperUrgent ? (
          <span
            className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 text-rose-500"
            aria-hidden="true"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={close}
            aria-hidden="true"
          />
          <div
            role="menu"
            aria-label="Ops action queue"
            className="absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-1.5rem)] bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden z-50 animate-scale-in"
          >
            <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-zinc-900 dark:text-white text-sm">
                  Action queue
                </h3>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {totalActionable === 0
                    ? 'All clear · nothing needs action'
                    : `${totalActionable} ${
                        totalActionable === 1 ? 'item' : 'items'
                      } need attention`}
                </p>
              </div>
              {totalActionable === 0 ? (
                <FiCheckCircle
                  className="h-5 w-5 text-emerald-500"
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <div className="max-h-[420px] overflow-y-auto p-2 space-y-2">
              {items.map((item, idx) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  isFocused={idx === focusIndex}
                  focusRef={rowRefs.current[idx]}
                  onFocus={() => setFocusIndex(idx)}
                  onActivate={activate}
                />
              ))}
            </div>
            <div className="px-4 py-2 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400">
              <span>Sorted by urgency</span>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/command-center');
                }}
                className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                Open Command Center
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
